<script setup lang="ts">
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useContentStore } from '@/stores/content.store'
import { useFrontpageStore } from '@/stores/frontpage.store'
import { usePreviewStore } from '@/stores/preview.store'
import { useNavigationStore } from '@/stores/navigation.store'
import { useSiteSettingsStore } from '@/stores/site-settings.store'
import { useRouterFocusReset } from '@/composables/useRouterFocusReset'
import { useRouteSeoContent } from '@/composables/useRouteSeoContent'
import { ensureCookieConsentReady, showCookieConsentIfNeeded } from '@/bootstrap/deferredClientModules'
import { getSeoDefaults, getSeoSiteName, getSeoSiteOrigin } from '@/config/seo'
import { env } from '@/utils/env'
import buildSeoHead, { resolveSeoTitle } from '@/utils/seo'

const store = useContentStore()
const { isLoading, pages } = storeToRefs(store)
const frontpageStore = useFrontpageStore()
const { frontpage } = storeToRefs(frontpageStore)
const previewStore = usePreviewStore()
const { document: previewDocument } = storeToRefs(previewStore)
const navigationStore = useNavigationStore()
const siteSettingsStore = useSiteSettingsStore()

import { useRoute, useRouter } from 'vue-router'
import { useUIStore } from '@/stores/ui.store'

const ui = useUIStore()
const router = useRouter()
const route = useRoute()
const normalizedRouteName = computed(() => {
  if (typeof route.name !== 'string') return ''
  return route.name.replace(/-(en|no)$/, '')
})

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function animationFrame() {
  return new Promise((resolve) => window.requestAnimationFrame(resolve))
}

async function waitForRouteDomPaint() {
  await nextTick()
  await animationFrame()
  await animationFrame()
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && ui.isOffcanvasOpen) {
    ui.closeOffcanvas()
  }
}

onMounted(async () => {
  try {
    await Promise.all([
      siteSettingsStore.ensure(),
      navigationStore.ensure(),
      store.ensureContent(),
      normalizedRouteName.value === 'home' ? frontpageStore.ensure() : Promise.resolve(),
      env.isMock ? delay(650) : Promise.resolve(),
    ])
  } finally {
    isHeaderReady.value = true
    await ensureCookieConsentReady()
    isCookieUiReady.value = true
    await nextTick()
    await showCookieConsentIfNeeded()
  }

  window.addEventListener('keydown', handleKeydown)

  router.afterEach(() => {
    ui.closeOffcanvas()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  stopBackendRetryInterval()
})

import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
const { locale, t } = useI18n()
const { attachments, currentRouteContent } = useRouteSeoContent()

useRouterFocusReset()

useHead(() => {
  const isNotFound = normalizedRouteName.value === '404'
  const isPreview = normalizedRouteName.value === 'preview'
  const siteName = getSeoSiteName()
  const seoContent = isPreview ? previewDocument.value : currentRouteContent.value
  const defaults = getSeoDefaults(
    isNotFound
      ? {
          title: t('notFound.seoTitle', { siteName }),
          description: t('notFound.description'),
        }
      : isPreview
        ? {
            title: t('preview.status'),
            description: t('preview.description'),
          }
        : undefined,
  )
  const head = buildSeoHead({
    routePath: route.fullPath,
    locale: String(locale.value),
    siteName,
    siteOrigin: getSeoSiteOrigin(),
    includeCanonical: !isPreview,
    content: seoContent,
    attachments: attachments.value,
    defaults,
  })

  if (!isNotFound && !isPreview) return head
  const meta = Array.isArray(head.meta) ? head.meta : []

  return {
    ...head,
    title: isPreview && previewDocument.value
      ? t('preview.seoTitle', {
          title: resolveSeoTitle(previewDocument.value, defaults.title),
        })
      : head.title,
    meta: [
      ...meta,
      {
        name: 'robots',
        content: 'noindex, nofollow, max-image-preview:large',
      },
    ],
  }
})

function removeBuildTimeJsonLdFallback() {
  if (typeof document === 'undefined') return

  document.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"][data-build-time-jsonld="true"]',
  ).forEach((script) => {
    script.remove()
  })
}

watch(
  () => [route.fullPath, currentRouteContent.value],
  async () => {
    await nextTick()
    removeBuildTimeJsonLdFallback()
  },
  { flush: 'post', immediate: true },
)

import { useLayout } from '@/composables/useLayout'
import AppPreloader from '@/components/AppPreloader.vue'
import AppModal from '@/components/modal/AppModal.vue'
import AppHeader from './layout/partials/AppHeader.vue'
import AppFooter from './layout/partials/AppFooter.vue'
import CookieButton from './components/common/CookieButton.vue'
import Notice from './components/common/Notice.vue'

const { layoutComponent } = useLayout()
const isHeaderReady = ref(false)
const isCookieUiReady = ref(false)
const isRouteDomReady = ref(false)
const backendRetryIntervalMs = 30_000
let backendRetryInterval: number | undefined
let isBackendRetrying = false

const hasRouteContent = computed(() => {
  switch (normalizedRouteName.value) {
    case 'home': {
      const frontpageId = frontpage.value?.frontpage_id
      return typeof frontpageId === 'number' && pages.value.some((page) => page.id === frontpageId)
    }
    case 'page':
    case 'post':
      return Boolean(currentRouteContent.value)
    case '404':
    case 'preview':
      return true
    default:
      return pages.value.length > 0
  }
})

const isWaitingForBackendContent = computed(() =>
  ui.isBackendUnavailable && !hasRouteContent.value,
)

const isPreloaderVisible = computed(() =>
  !isHeaderReady.value
  || isLoading.value
  || isWaitingForBackendContent.value
  || !isRouteDomReady.value,
)

watch(
  () => [route.fullPath, hasRouteContent.value],
  async ([fullPath, hasContent]) => {
    const expectedFullPath = String(fullPath)
    isRouteDomReady.value = false

    if (!hasContent) return

    await waitForRouteDomPaint()

    if (route.fullPath === expectedFullPath && hasRouteContent.value) {
      isRouteDomReady.value = true
    }
  },
  { flush: 'post', immediate: true },
)

async function retryBackendContentLoad() {
  if (isBackendRetrying) return

  isBackendRetrying = true

  try {
    await Promise.all([
      siteSettingsStore.ensure({ force: true }),
      navigationStore.ensure({ force: true }),
      store.ensureContent({ force: true }),
      normalizedRouteName.value === 'home'
        ? frontpageStore.ensure({ force: true })
        : Promise.resolve(),
    ])
  } finally {
    isBackendRetrying = false
  }
}

function stopBackendRetryInterval() {
  if (backendRetryInterval === undefined) return

  window.clearInterval(backendRetryInterval)
  backendRetryInterval = undefined
}

watch(
  () => isWaitingForBackendContent.value,
  (shouldRetry) => {
    stopBackendRetryInterval()

    if (!shouldRetry) return

    backendRetryInterval = window.setInterval(() => {
      void retryBackendContentLoad()
    }, backendRetryIntervalMs)
  },
  { immediate: true },
)

watch(
  () => locale.value,
  async (nextLocale, previousLocale) => {
    if (!previousLocale || nextLocale === previousLocale) return

    await Promise.all([
      siteSettingsStore.ensure({ force: true }),
      navigationStore.ensure({ force: true }),
      store.ensureContent({ force: true }),
      frontpageStore.ensure({ force: true }),
    ])
  },
  { flush: 'sync' },
)

</script>

<template>
  <AppPreloader v-if="isPreloaderVisible" />
  <AppHeader v-if="isHeaderReady" />
  <component 
    :is="layoutComponent" 
  />
  <AppModal />
  <CookieButton v-if="isCookieUiReady" />
  <AppFooter />
  <Notice v-if="!isPreloaderVisible" />
</template>
